import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { Table, Empty, Button, Modal, Spin, message } from 'antd';
import { EyeOutlined, DownloadOutlined } from '@ant-design/icons';
import { useDispatch } from 'react-redux';
import moment from 'moment';
import styled from 'styled-components';
import { Box, Text } from 'atoms';
import { searchVendorBillFieldChanges } from 'store/vendorBillApprovalSlice';
import { getSignedUrl } from 'store/commonSlice';

const HistoryCard = styled(Box)`
	background-color: #fff;
	border-radius: 8px;
	border-top: 2px solid #2f8ecb;
	margin-bottom: 16px;
	padding: 24px;
`;

const SubTitle = styled(Text)`
	font-size: 20px;
	font-weight: 400;
	line-height: 30px;
	margin-bottom: 16px;
	display: block;
`;

const DocumentPreviewModal = styled(Modal)`
	.ant-modal-body {
		text-align: center;
		padding: 24px;
		background: #f5f5f5;
	}
`;

const PreviewImage = styled.img`
	max-width: 100%;
	max-height: 70vh;
	width: auto;
	height: auto;
	object-fit: contain;
	border: 1px solid #d9d9d9;
	border-radius: 4px;
	background: #fff;
	display: block;
	margin: 0 auto;
`;

const PreviewIframe = styled.iframe`
	width: 100%;
	height: 600px;
	border: 1px solid #d9d9d9;
	border-radius: 4px;
	background: #fff;
`;

const ImageContainer = styled.div`
	display: flex;
	justify-content: center;
	align-items: center;
	min-height: 400px;
	background: #f5f5f5;
	border: 1px solid #d9d9d9;
	border-radius: 4px;
	padding: 20px;
	overflow: auto;
`;

const NO_DOCUMENT_URL_MSG = 'No document URL provided';
const VENDOR_BILL_AUDIT_PREFIX = 'vendor_bill_audit/';

function EditHistoryTable({ vendorBillId, useV2API = false }) {
	const dispatch = useDispatch();
	const [loading, setLoading] = useState(false);
	const [editHistory, setEditHistory] = useState([]);
	const [previewVisible, setPreviewVisible] = useState(false);
	const [previewUrl, setPreviewUrl] = useState('');
	const [previewTitle, setPreviewTitle] = useState('');
	const [imageError, setImageError] = useState(false);
	const [loadingSignedUrl, setLoadingSignedUrl] = useState(false);

	useEffect(() => {
		if (vendorBillId) {
			fetchEditHistory();
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [vendorBillId, useV2API]);

	// Helper to extract audit metadata
	const extractAuditMetadata = (audit) => {
		const {
			changes = {},
			id: auditId,
			remark,
			status,
			created_by: createdBy,
			created_at: createdAt,
			createdAt: createdAtAlt,
			reviewed_by: reviewedBy,
			reviewedBy: reviewedByAlt,
			reviewed_at: reviewedAt,
			reviewedAt: reviewedAtAlt,
			image_urls,
			image_keys,
			updated_by_username,
			created_by_username,
			updated_by_name,
			created_by_name,
			updated_by: updatedBy,
		} = audit || {};

		// Get updated by username with fallback chain
		const updatedByUsername =
			updated_by_username ||
			created_by_username ||
			updated_by_name ||
			created_by_name ||
			`User ${updatedBy || createdBy || 'N/A'}`;

		return {
			changes,
			auditId,
			remark,
			status,
			createdBy,
			createdAt: createdAt || createdAtAlt,
			reviewedBy: reviewedBy || reviewedByAlt,
			reviewedAt: reviewedAt || reviewedAtAlt,
			image_urls,
			image_keys,
			updatedByUsername,
		};
	};

	// Helper to get docs for a field.
	// Priority: image_keys (plain S3 key) → image_urls (full URL, legacy)
	const getDocsForField = (fieldName, imageKeys, imageUrls) => {
		// Check image_keys first (new API: { tds_amount: "uuid-filename.png" })
		if (imageKeys && typeof imageKeys === 'object') {
			const s3Key = imageKeys[fieldName];
			if (s3Key && typeof s3Key === 'string') {
				return { url: s3Key, fieldName };
			}
		}

		// Fallback: image_urls (legacy full-URL format)
		if (imageUrls && typeof imageUrls === 'object') {
			const imageUrl = imageUrls[fieldName];
			if (imageUrl && typeof imageUrl === 'string') {
				return { url: imageUrl, fieldName };
			}
		}

		return null;
	};

	// Helper to extract change values
	const extractChangeValues = (change) => {
		const oldValue = change?.old !== undefined ? change.old : change?.initial;
		const newValue = change?.new !== undefined ? change.new : change?.updated;
		return { oldValue, newValue };
	};

	// Transform audit data from JSONB changes format to table rows
	const transformAuditData = (auditRecords) => {
		if (!Array.isArray(auditRecords)) {
			return [];
		}

		const transformedRows = [];

		auditRecords.forEach((audit) => {
			const metadata = extractAuditMetadata(audit);

			// If no changes object, skip this audit record
			if (Object.keys(metadata.changes).length === 0) {
				return;
			}

			// Create one row per changed field
			Object.keys(metadata.changes).forEach((fieldName) => {
				const change = metadata.changes[fieldName];
				const { oldValue, newValue } = extractChangeValues(change);
				const docsForField = getDocsForField(
					fieldName,
					metadata.image_keys,
					metadata.image_urls
				);

				transformedRows.push({
					id: `${metadata.auditId}_${fieldName}`, // Unique key for each field change
					audit_id: metadata.auditId,
					edited_field: fieldName,
					initial_value: oldValue,
					updated_value: newValue,
					updated_by: metadata.updatedByUsername, // Use updated_by_username from API
					updated_by_username: metadata.updatedByUsername, // Direct mapping to API field
					updated_at: metadata.createdAt,
					reviewed_by: metadata.reviewedBy,
					reviewed_at: metadata.reviewedAt,
					remark: metadata.remark,
					status: metadata.status,
					docs: docsForField,
				});
			});
		});

		return transformedRows;
	};

	const fetchEditHistory = async () => {
		setLoading(true);
		try {
			const action = searchVendorBillFieldChanges({
				id: vendorBillId,
				status: ['accepted', 'pending'], // Only show pending and paid changes, exclude accepted/rejected
				useV2API,
			});

			const response = await dispatch(action);
			const { data, rows } = response || {};
			const auditRecords = data || rows || [];
			const transformedHistory = transformAuditData(
				Array.isArray(auditRecords) ? auditRecords : []
			);
			setEditHistory(transformedHistory);
		} catch (error) {
			console.error('Error fetching edit history:', error);
			setEditHistory([]);
		} finally {
			setLoading(false);
		}
	};

	// Extract S3 key from URL and remove entity prefix (since API adds it)
	const extractS3Key = (url) => {
		if (!url) return null;

		try {
			let extractedKey = null;

			// If it's already an S3 key (starts with vendor_bill_audit/)
			if (url.startsWith(VENDOR_BILL_AUDIT_PREFIX)) {
				extractedKey = url;
			}
			// If it's a full S3 URL, extract the key
			else if (url.includes('amazonaws.com')) {
				// Extract path after amazonaws.com/
				const urlObj = new URL(url);
				const { pathname } = urlObj;
				// Remove leading slash and get the key
				extractedKey = pathname.startsWith('/') ? pathname.substring(1) : pathname;
			}
			// If it's a relative path, assume it's already a key
			else {
				extractedKey = url;
			}

			if (!extractedKey) return null;

			// Remove entity prefix if present (API will add it based on entity parameter)
			// The entity is 'vendor_bill_audit', so remove 'vendor_bill_audit/' prefix
			if (extractedKey.startsWith(VENDOR_BILL_AUDIT_PREFIX)) {
				extractedKey = extractedKey.substring(VENDOR_BILL_AUDIT_PREFIX.length);
			}

			return extractedKey;
		} catch (error) {
			console.error('Error extracting S3 key:', error);
			return null;
		}
	};

	const handleViewDocument = async (docUrl, docName) => {
		if (!docUrl) {
			console.warn(NO_DOCUMENT_URL_MSG);
			message.error(NO_DOCUMENT_URL_MSG);
			return;
		}

		setLoadingSignedUrl(true);
		setImageError(false);
		setPreviewTitle(docName || 'Document Preview');
		setPreviewVisible(true);

		try {
			// Extract S3 key from URL
			const s3Key = extractS3Key(docUrl);

			if (!s3Key) {
				console.error('Could not extract S3 key from URL:', docUrl);
				message.error('Invalid document URL');
				setLoadingSignedUrl(false);
				setImageError(true);
				return;
			}

			console.log('Getting signed URL for:', { originalUrl: docUrl, s3Key });

			// Get signed URL for viewing
			const response = await dispatch(
				getSignedUrl({
					url_type: 'view',
					data: {
						entity: 'vendor_bill',
						keys: [s3Key],
					},
				})
			);

			// Extract signed URL from response
			const signedUrl =
				(response && response.status === 'success' && response.data?.[0]?.url) ||
				(response?.data &&
					Array.isArray(response.data) &&
					response.data.length > 0 &&
					response.data[0]?.url)
					? response.data[0].url
					: null;

			if (signedUrl) {
				console.log('Signed URL obtained successfully');
				setPreviewUrl(signedUrl);
			} else {
				console.error('Failed to get signed URL from response:', response);
				message.error('Failed to load document preview');
				setImageError(true);
				setPreviewUrl(''); // Clear URL to show error state
			}
		} catch (error) {
			console.error('Error getting signed URL:', error);
			message.error('Failed to load document preview');
			setImageError(true);
			setPreviewUrl(''); // Clear URL to show error state
		} finally {
			setLoadingSignedUrl(false);
		}
	};

	const handleDownloadDocument = async (docUrl, docName) => {
		if (!docUrl) {
			message.error(NO_DOCUMENT_URL_MSG);
			return;
		}

		try {
			// Extract S3 key from URL
			const s3Key = extractS3Key(docUrl);

			if (!s3Key) {
				console.error('Could not extract S3 key from URL:', docUrl);
				message.error('Invalid document URL');
				return;
			}

			// Get signed URL for download
			const response = await dispatch(
				getSignedUrl({
					url_type: 'download',
					data: {
						entity: 'vendor_bill',
						keys: [s3Key],
					},
				})
			);

			// Extract signed URL from response
			const signedUrl =
				(response && response.status === 'success' && response.data?.[0]?.url) ||
				(response?.data &&
					Array.isArray(response.data) &&
					response.data.length > 0 &&
					response.data[0]?.url)
					? response.data[0].url
					: null;

			if (signedUrl) {
				const link = document.createElement('a');
				link.href = signedUrl;
				link.download = docName || 'document';
				link.target = '_blank';
				document.body.appendChild(link);
				link.click();
				document.body.removeChild(link);
			} else {
				message.error('Failed to get download URL');
			}
		} catch (error) {
			console.error('Error downloading document:', error);
			message.error('Failed to download document');
		}
	};

	const getFileExtension = (url) => {
		if (!url) return '';
		try {
			// Decode URL to handle %20 and other encoded characters
			const decodedUrl = decodeURIComponent(url);
			// Remove query parameters
			const urlWithoutQuery = decodedUrl.split('?')[0];
			// Get extension
			return urlWithoutQuery.split('.').pop()?.toLowerCase() || '';
		} catch (error) {
			// Fallback if decodeURIComponent fails
			const urlWithoutQuery = url.split('?')[0];
			return urlWithoutQuery.split('.').pop()?.toLowerCase() || '';
		}
	};

	// Check if URL is likely an image based on URL pattern or content type
	const isLikelyImage = (url) => {
		if (!url) return false;
		const fileExt = getFileExtension(url);
		const imageExtensions = ['jpg', 'jpeg', 'png', 'gif', 'webp', 'bmp', 'svg'];

		// Check extension
		if (imageExtensions.includes(fileExt)) {
			return true;
		}

		// Check if URL contains image-related keywords
		const urlLower = url.toLowerCase();
		return (
			urlLower.includes('image') ||
			urlLower.includes('photo') ||
			urlLower.includes('picture') ||
			url.includes('Content-Type=image') ||
			url.includes('content-type=image')
		);
	};

	const renderPreviewContent = () => {
		if (loadingSignedUrl) {
			return (
				<div style={{ padding: '40px', textAlign: 'center' }}>
					<Spin size="large" tip="Loading document..." />
				</div>
			);
		}

		if (!previewUrl) {
			return (
				<div style={{ padding: '40px', textAlign: 'center' }}>
					<Text type="secondary">No document URL available</Text>
				</div>
			);
		}

		const fileExt = getFileExtension(previewUrl);
		const isPdf = fileExt === 'pdf';
		const isImage =
			['jpg', 'jpeg', 'png', 'gif', 'webp'].includes(fileExt) || isLikelyImage(previewUrl);

		// If URL is from S3 (amazonaws.com) and not a PDF, assume it's an image
		// since the API provides image_urls for TDS documents
		const isS3Image = previewUrl.includes('amazonaws.com') && !isPdf;

		if (isPdf) {
			return (
				<div>
					<div style={{ marginBottom: '16px' }}>
						<Button
							type="primary"
							onClick={() => window.open(previewUrl, '_blank')}
							size="large"
						>
							Open PDF in New Tab
						</Button>
					</div>
					<PreviewIframe
						title={previewTitle}
						src={`${previewUrl}#toolbar=0&navpanes=0&scrollbar=0`}
					/>
				</div>
			);
		}

		// Try to render as image if it's likely an image or from S3 (which provides images)
		if (isImage || isS3Image) {
			if (imageError) {
				return (
					<ImageContainer>
						<div style={{ padding: '20px' }}>
							<p style={{ marginBottom: '16px', color: '#ff4d4f' }}>
								Failed to load image. Please try opening in a new tab.
							</p>
							<Button
								type="primary"
								onClick={() => window.open(previewUrl, '_blank')}
							>
								Open in New Tab
							</Button>
						</div>
					</ImageContainer>
				);
			}

			return (
				<ImageContainer>
					<PreviewImage
						src={previewUrl}
						alt={previewTitle}
						onError={(e) => {
							console.error('Image load error for URL:', previewUrl);
							console.error('Error details:', e);
							setImageError(true);
						}}
						onLoad={() => {
							console.log('Image loaded successfully:', previewUrl);
							setImageError(false);
						}}
						style={{
							maxWidth: '100%',
							maxHeight: '70vh',
							width: 'auto',
							height: 'auto',
						}}
					/>
				</ImageContainer>
			);
		}

		// Fallback: try to render as image anyway if we're not sure
		// (better to try and fail than to show "not available")
		return (
			<ImageContainer>
				<PreviewImage
					src={previewUrl}
					alt={previewTitle}
					onError={() => {
						console.error('Failed to load as image, showing fallback:', previewUrl);
						setImageError(true);
					}}
					onLoad={() => {
						console.log('Image loaded successfully (fallback):', previewUrl);
						setImageError(false);
					}}
					style={{
						maxWidth: '100%',
						maxHeight: '70vh',
						width: 'auto',
						height: 'auto',
					}}
				/>
				{imageError && (
					<div style={{ padding: '20px', marginTop: '20px' }}>
						<p style={{ marginBottom: '16px', color: '#ff4d4f' }}>
							Failed to load image. Please try opening in a new tab.
						</p>
						<Button type="primary" onClick={() => window.open(previewUrl, '_blank')}>
							Open in New Tab
						</Button>
					</div>
				)}
			</ImageContainer>
		);
	};

	const formatCurrency = (value) => {
		if (value === null || value === undefined || value === '') return '-';
		const numValue = typeof value === 'string' ? parseFloat(value) : value;
		return `₹${numValue.toLocaleString('en-IN', {
			minimumFractionDigits: 2,
			maximumFractionDigits: 2,
		})}`;
	};

	const columns = [
		{
			title: 'Edited Field',
			dataIndex: 'edited_field',
			key: 'edited_field',
			render: (text) => <Text>{text || '-'}</Text>,
		},
		{
			title: 'Initial Value',
			dataIndex: 'initial_value',
			key: 'initial_value',
			render: (value) => {
				if (
					typeof value === 'number' ||
					(typeof value === 'string' && !Number.isNaN(Number(value)))
				) {
					return <Text>{formatCurrency(value)}</Text>;
				}
				return <Text>{value || '-'}</Text>;
			},
		},
		{
			title: 'Updated Value',
			dataIndex: 'updated_value',
			key: 'updated_value',
			render: (value) => {
				if (
					typeof value === 'number' ||
					(typeof value === 'string' && !Number.isNaN(Number(value)))
				) {
					return <Text>{formatCurrency(value)}</Text>;
				}
				return <Text>{value || '-'}</Text>;
			},
		},
		{
			title: 'Updated By',
			dataIndex: 'updated_by_username',
			key: 'updated_by_username',
			render: (text) => <Text>{text || '-'}</Text>,
		},
		{
			title: 'Updated At',
			dataIndex: 'updated_at',
			key: 'updated_at',
			render: (date) => {
				if (!date) return <Text>-</Text>;
				return <Text>{moment(date).format('DD-MM-YYYY h:mm A')}</Text>;
			},
		},
		{
			title: 'Docs',
			dataIndex: 'docs',
			key: 'docs',
			render: (docs) => {
				if (!docs || !docs.url) return <Text>-</Text>;

				const docUrl = docs.url;
				const docName = docUrl.split('/').pop()?.split('?')[0] || 'Document';

				return (
					<div
						style={{
							display: 'flex',
							gap: '8px',
							alignItems: 'center',
						}}
					>
						<Button
							type="link"
							icon={<EyeOutlined />}
							onClick={() => handleViewDocument(docUrl, docName)}
							title="View Document"
							size="small"
						/>
						<Button
							type="link"
							icon={<DownloadOutlined />}
							onClick={() => handleDownloadDocument(docUrl, docName)}
							title="Download Document"
							size="small"
						/>
					</div>
				);
			},
		},
	];

	// Don't render the table if there's no edit history (after loading is complete)
	if (!loading && editHistory.length === 0) {
		return null;
	}

	return (
		<>
			<HistoryCard>
				<SubTitle>
					Edit History ({editHistory.length} CHANGE{editHistory.length !== 1 ? 'S' : ''})
				</SubTitle>
				<Table
					columns={columns}
					dataSource={editHistory}
					loading={loading}
					rowKey={(record, index) => record.id || record.edit_history_id || index}
					pagination={false}
					locale={{
						emptyText: (
							<Empty
								description={<Text type="secondary">No edit history found</Text>}
							/>
						),
					}}
				/>
			</HistoryCard>

			<DocumentPreviewModal
				open={previewVisible}
				title={previewTitle}
				footer={null}
				onCancel={() => {
					setPreviewVisible(false);
					setPreviewUrl('');
					setPreviewTitle('');
					setImageError(false);
				}}
				centered
				width={getFileExtension(previewUrl) === 'pdf' ? 1000 : 800}
				destroyOnClose
			>
				{renderPreviewContent()}
			</DocumentPreviewModal>
		</>
	);
}

EditHistoryTable.propTypes = {
	vendorBillId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
	useV2API: PropTypes.bool,
};

export default EditHistoryTable;
